// Sticky footer for the OT detail page. Shows a friendly summary of
// where the user is and what they still need to do — split between
// "documents" (open the Documents modal) and "form fields" (inline
// blockers in the current stage panel).

import { useMemo } from "react"
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  computeClientBlockers,
  mergeBlockers,
  parseBlocker,
  resolveBlocker,
} from "@/features/transit/lib/blockers"
import { useAdvanceOT } from "@/features/transit/hooks/use-transit-mutations"
import { isStageUnlocked, prevStage } from "@/features/transit/lib/stages"
import { cn } from "@/lib/utils"
import type { BlockerDTO, OT, OTStage } from "@/features/transit/api/types"

interface StageActionsRowProps {
  ot: OT
  // Local "go back" handler — this is purely a panel selector now.
  // Clicking it does NOT mutate the OT's real stage; that's owned by
  // Advance only. The page caller swaps in the previous stage's panel.
  onGoBack?: (stage: OTStage) => void
}

export function StageActionsRow({ ot, onGoBack }: StageActionsRowProps) {
  const { t } = useTranslation("transit")
  const advance = useAdvanceOT(ot.id)

  // Combine server blockers with locally-computed ones so the footer
  // reflects validation rules the FE can decide without a refetch
  // (e.g. BV lots missing their CCC document).
  const blockers = useMemo(
    () => mergeBlockers(ot.blockers, computeClientBlockers(ot)),
    [ot]
  )
  const isReady = blockers.length === 0
  const isTerminer = ot.stage === "TERMINER"

  const back = prevStage(ot.stage)
  const canGoBack = back && isStageUnlocked(ot.unlockedStages, back)

  // Split blockers: missingDoc → "open Documents", everything else → inline.
  const { docCount, formBlockers } = useMemo(
    () => splitBlockers(blockers),
    [blockers]
  )

  // The Terminer stage is read-only — Back to a finalised stage is
  // counter-productive and Advance is N/A. Hide the footer entirely
  // and let the user navigate via the stepper if they really need to.
  if (isTerminer) return null

  return (
    <>
      <div
        className={cn(
          "sticky bottom-0 z-20 -mx-6 mt-4 -mb-6 border-t bg-muted/70 px-6 py-3 backdrop-blur"
        )}
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            {isReady ? (
              <ReadyMessage stageLabel={t(`stages.${ot.stage}`)} />
            ) : (
              <BlockerSummary docCount={docCount} formBlockers={formBlockers} />
            )}
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            {canGoBack && back && onGoBack ? (
              <Button variant="ghost" size="sm" onClick={() => onGoBack(back)}>
                <ArrowLeft className="size-4" />
                {t("stageActions.backTo", { stage: t(`stages.${back}`) })}
              </Button>
            ) : null}

            {!isTerminer ? (
              <Button
                size="sm"
                onClick={() => advance.mutate()}
                disabled={advance.isPending || !isReady}
              >
                {advance.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ArrowRight className="size-4" />
                )}
                {ot.stage === "REMISE"
                  ? t("stageActions.closeAndArchive")
                  : t("stageActions.advance")}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

function ReadyMessage({ stageLabel }: { stageLabel: string }) {
  const { t } = useTranslation("transit")
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
      <CheckCircle2 className="size-4 flex-shrink-0 text-emerald-600" />
      <span>{t("stageActions.ready", { stage: stageLabel })}</span>
    </div>
  )
}

function BlockerSummary({
  docCount,
  formBlockers,
}: {
  docCount: number
  formBlockers: BlockerDTO[]
}) {
  const { t } = useTranslation("transit")
  const totalCount = docCount + formBlockers.length

  // Show up to three form blockers inline with their resolved labels;
  // anything else collapses into "+N more".
  const visibleForm = formBlockers.slice(0, 3)
  const extraForm = formBlockers.length - visibleForm.length

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <AlertTriangle className="size-4 flex-shrink-0 text-amber-600" />
        <span>{t("stageActions.blockersTitle", { count: totalCount })}</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground/90">
        {docCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-foreground/80">
            <span aria-hidden className="text-amber-600">
              •
            </span>
            {t("stageActions.docsBlocker", { count: docCount })}
          </span>
        ) : null}

        {visibleForm.map((b, i) => (
          <FormBlockerChip key={`${b.code}-${i}`} blocker={b} />
        ))}

        {extraForm > 0 ? (
          <span className="text-muted-foreground">
            {t("stageActions.moreBlockers", { count: extraForm })}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function FormBlockerChip({ blocker }: { blocker: BlockerDTO }) {
  const { t } = useTranslation("transit")
  const resolved = resolveBlocker(blocker)
  const text = t(resolved.i18nKey, {
    defaultValue: resolved.fallback,
    ...resolved.values,
  })
  return (
    <span className="inline-flex items-center gap-1 text-foreground/80">
      <span aria-hidden className="text-amber-600">
        •
      </span>
      {text}
    </span>
  )
}

interface SplitBlockers {
  docCount: number
  formBlockers: BlockerDTO[]
}

function splitBlockers(blockers: BlockerDTO[]): SplitBlockers {
  let docCount = 0
  const formBlockers: BlockerDTO[] = []
  for (const b of blockers) {
    const parsed = parseBlocker(b)
    if (parsed.kind === "missingDoc") docCount += 1
    else formBlockers.push(b)
  }
  return { docCount, formBlockers }
}
